// /src/store/fileManagment/actions.ts

import { Dispatch } from 'redux';
import {RootState} from '../generalReducer';

import {
  START_UPLOAD,
  UPDATE_UPLOAD_PROGRESS,
  UPDATE_UPLOAD_STATUS,
  COMPLETE_UPLOAD,
  FAIL_UPLOAD,
  CANCEL_UPLOAD,
  REMOVE_UPLOAD,
  RETRY_UPLOAD,
  PAUSE_UPLOAD,
  RESUME_UPLOAD,
  SET_UPLOAD_CHUNK_INFO,
  FileUploadActionTypes,
  UploadStatus,
  UploadState,
  FileType
} from './types';

// Action pour démarrer un nouvel upload
export const startUpload = (
  id: string,
  fileName: string,
  fileSize: number,
  fileType: FileType,
  mimeType: string,
  totalChunks: number,
  chunkSize: number,
  maxRetries: number = 3,
  metadata?: Record<string, any>,
  entityId?: number,
  entityType?: string
) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: START_UPLOAD,
    payload: {
      id,
      fileName,
      fileSize,
      fileType,
      mimeType,
      totalChunks,
      chunkSize,
      maxRetries,
      metadata,
      entityId,
      entityType
    }
  });
};

// Action pour mettre à jour la progression d'un upload
export const updateUploadProgress = (
  id: string,
  progress: number,
  uploadedBytes: number,
  uploadSpeed?: number,
  estimatedTimeRemaining?: number
) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: UPDATE_UPLOAD_PROGRESS,
    payload: {
      id,
      progress,
      uploadedBytes,
      uploadSpeed,
      estimatedTimeRemaining
    }
  });
};

// Action pour mettre à jour le statut d'un upload
export const updateUploadStatus = (
  id: string,
  status: UploadStatus,
  error?: string
) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: UPDATE_UPLOAD_STATUS,
    payload: {
      id,
      status,
      error
    }
  });
};

// Action pour marquer un upload comme terminé
export const completeUpload = (id: string) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: COMPLETE_UPLOAD,
    payload: { id }
  });
};

// Action pour marquer un upload comme échoué
export const failUpload = (id: string, error: string) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: FAIL_UPLOAD,
    payload: { id, error }
  });
};

// Action pour annuler un upload
export const cancelUpload = (id: string) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: CANCEL_UPLOAD,
    payload: { id }
  });
};

// Action pour supprimer un upload du store
export const removeUpload = (id: string) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: REMOVE_UPLOAD,
    payload: { id }
  });
};

// Action pour relancer un upload échoué
export const retryUpload = (id: string) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: RETRY_UPLOAD,
    payload: { id }
  });
};

// Action pour mettre en pause un upload
export const pauseUpload = (id: string) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: PAUSE_UPLOAD,
    payload: { id }
  });
};

// Action pour reprendre un upload en pause
export const resumeUpload = (id: string) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: RESUME_UPLOAD,
    payload: { id }
  });
};

// Action pour mettre à jour les informations d'un chunk
export const setUploadChunkInfo = (
  id: string,
  chunkIndex: number,
  uploaded: boolean,
  retryCount?: number
) => (dispatch: Dispatch<FileUploadActionTypes>) => {
  dispatch({
    type: SET_UPLOAD_CHUNK_INFO,
    payload: {
      id,
      chunkIndex,
      uploaded,
      retryCount
    }
  });
};

// Actions composées pour des opérations communes

// Nettoyer tous les uploads terminés
export const clearCompletedUploads = () => (dispatch: any, getState: any) => {
  const { fileUploadReducer } = getState();
  const completedUploads = Object.keys(fileUploadReducer.uploads).filter(
    id => fileUploadReducer.uploads[id].status === UploadStatus.COMPLETED
  );
  
  completedUploads.forEach(id => {
    dispatch(removeUpload(id));
  });
};

// Annuler tous les uploads en cours
export const cancelAllUploads = () => (dispatch: any, getState: any) => {
  const { fileUploadReducer } = getState();
  const activeUploads = Object.keys(fileUploadReducer.uploads).filter(
    id => [UploadStatus.UPLOADING, UploadStatus.PENDING, UploadStatus.PAUSED, UploadStatus.RETRYING]
      .includes(fileUploadReducer.uploads[id].status)
  );
  
  activeUploads.forEach(id => {
    dispatch(cancelUpload(id));
  });
};

// Relancer tous les uploads échoués
export const retryAllFailedUploads = () => (dispatch: any, getState: any) => {
  const { fileUploadReducer } = getState();
  const failedUploads = Object.keys(fileUploadReducer.uploads).filter(
    id => fileUploadReducer.uploads[id].status === UploadStatus.FAILED
  );
  
  failedUploads.forEach(id => {
    dispatch(retryUpload(id));
  });
};

// Sélecteur pour récupérer tout le state des uploads
export const getFileUploadState = (state: RootState) => state.fileUploadReducer;

// Sélecteur pour récupérer tous les uploads
export const getAllUploads = (state: RootState): UploadState[] => 
  Object.values(state.fileUploadReducer.uploads);

// Sélecteur pour récupérer un upload spécifique par son ID
export const getUploadById = (state: RootState, uploadId: string): UploadState | undefined => 
  state.fileUploadReducer.uploads[uploadId];

// Sélecteur pour récupérer les uploads actifs (en cours, en attente, en pause, en retry)
export const getActiveUploads = (state: RootState): UploadState[] => 
  Object.values(state.fileUploadReducer.uploads).filter(upload =>
    [UploadStatus.UPLOADING, UploadStatus.PENDING, UploadStatus.PAUSED, UploadStatus.RETRYING]
      .includes(upload.status)
  );

// Sélecteur pour récupérer les uploads en cours d'upload
export const getUploadingFiles = (state: RootState): UploadState[] => 
  Object.values(state.fileUploadReducer.uploads).filter(upload =>
    upload.status === UploadStatus.UPLOADING
  );

// Sélecteur pour récupérer les uploads terminés
export const getCompletedUploads = (state: RootState): UploadState[] => 
  Object.values(state.fileUploadReducer.uploads).filter(upload =>
    upload.status === UploadStatus.COMPLETED
  );

// Sélecteur pour récupérer les uploads échoués
export const getFailedUploads = (state: RootState): UploadState[] => 
  Object.values(state.fileUploadReducer.uploads).filter(upload =>
    upload.status === UploadStatus.FAILED
  );

// Sélecteur pour récupérer les uploads annulés
export const getCancelledUploads = (state: RootState): UploadState[] => 
  Object.values(state.fileUploadReducer.uploads).filter(upload =>
    upload.status === UploadStatus.CANCELLED
  );

// Sélecteur pour récupérer les uploads en pause
export const getPausedUploads = (state: RootState): UploadState[] => 
  Object.values(state.fileUploadReducer.uploads).filter(upload =>
    upload.status === UploadStatus.PAUSED
  );

// Sélecteur pour récupérer les uploads par type de fichier
export const getUploadsByFileType = (state: RootState, fileType: FileType): UploadState[] => 
  Object.values(state.fileUploadReducer.uploads).filter(upload =>
    upload.fileType === fileType
  );

// Sélecteur pour récupérer les uploads d'un type d'entité spécifique
export const getUploadsByEntityType = (state: RootState, entityType: string): UploadState[] => 
  Object.values(state.fileUploadReducer.uploads).filter(upload =>
    upload.entityType === entityType
  );

// Sélecteur pour récupérer les uploads liés à une entité spécifique
export const getUploadsByEntityId = (state: RootState, entityId: number): UploadState[] => 
  Object.values(state.fileUploadReducer.uploads).filter(upload =>
    upload.entityId === entityId
  );

// Sélecteur pour récupérer le nombre total d'uploads actifs
export const getActiveUploadsCount = (state: RootState): number => 
  state.fileUploadReducer.activeUploadsCount;

// Sélecteur pour récupérer le nombre total d'uploads
export const getTotalUploadsCount = (state: RootState): number => 
  state.fileUploadReducer.totalUploadsCount;

// Sélecteur pour récupérer la progression globale
export const getGlobalProgress = (state: RootState): number => 
  state.fileUploadReducer.globalProgress;

// Sélecteur pour vérifier s'il y a des uploads en cours
export const hasActiveUploads = (state: RootState): boolean => 
  state.fileUploadReducer.activeUploadsCount > 0;

// Sélecteur pour vérifier s'il y a des uploads échoués
export const hasFailedUploads = (state: RootState): boolean => 
  Object.values(state.fileUploadReducer.uploads).some(upload =>
    upload.status === UploadStatus.FAILED
  );

// Sélecteur pour récupérer les statistiques globales
export const getUploadStatistics = (state: RootState) => {
  const uploads = Object.values(state.fileUploadReducer.uploads);
  
  return {
    total: uploads.length,
    active: uploads.filter(u => [UploadStatus.UPLOADING, UploadStatus.PENDING, UploadStatus.PAUSED, UploadStatus.RETRYING].includes(u.status)).length,
    completed: uploads.filter(u => u.status === UploadStatus.COMPLETED).length,
    failed: uploads.filter(u => u.status === UploadStatus.FAILED).length,
    cancelled: uploads.filter(u => u.status === UploadStatus.CANCELLED).length,
    paused: uploads.filter(u => u.status === UploadStatus.PAUSED).length,
    totalSizeBytes: uploads.reduce((sum, u) => sum + u.fileSize, 0),
    uploadedBytes: uploads.reduce((sum, u) => sum + u.uploadedBytes, 0),
    globalProgress: state.fileUploadReducer.globalProgress
  };
};

// Sélecteur pour récupérer la vitesse d'upload moyenne
export const getAverageUploadSpeed = (state: RootState): number => {
  const activeUploads = getUploadingFiles(state);
  if (activeUploads.length === 0) return 0;
  
  const totalSpeed = activeUploads.reduce((sum, upload) => 
    sum + (upload.uploadSpeed || 0), 0
  );
  
  return totalSpeed / activeUploads.length;
};

// Sélecteur pour récupérer le temps restant estimé global
export const getGlobalEstimatedTimeRemaining = (state: RootState): number => {
  const activeUploads = getUploadingFiles(state);
  if (activeUploads.length === 0) return 0;
  
  // Prendre le maximum des temps restants pour être conservateur
  const maxTimeRemaining = Math.max(...activeUploads.map(upload => 
    upload.estimatedTimeRemaining || 0
  ));
  
  return maxTimeRemaining;
};

// Sélecteur pour vérifier si on peut ajouter un nouvel upload
export const canStartNewUpload = (state: RootState): boolean => {
  const uploadingCount = getUploadingFiles(state).length;
  return uploadingCount < state.fileUploadReducer.maxConcurrentUploads;
};

// Sélecteur pour récupérer les uploads les plus récents
export const getRecentUploads = (state: RootState, limit: number = 10): UploadState[] => {
  return Object.values(state.fileUploadReducer.uploads)
    .sort((a, b) => b.startTime.getTime() - a.startTime.getTime())
    .slice(0, limit);
};

// Sélecteur pour récupérer les uploads triés par progression (pour affichage)
export const getUploadsSortedByProgress = (state: RootState): UploadState[] => {
  return Object.values(state.fileUploadReducer.uploads)
    .sort((a, b) => {
      // Prioriser par statut d'abord, puis par progression
      const statusOrder = {
        [UploadStatus.UPLOADING]: 0,
        [UploadStatus.RETRYING]: 1,
        [UploadStatus.PENDING]: 2,
        [UploadStatus.PAUSED]: 3,
        [UploadStatus.FAILED]: 4,
        [UploadStatus.CANCELLED]: 5,
        [UploadStatus.COMPLETED]: 6
      };
      
      const statusDiff = statusOrder[a.status] - statusOrder[b.status];
      if (statusDiff !== 0) return statusDiff;
      
      // Si même statut, trier par progression décroissante
      return b.progress - a.progress;
    });
};

// Sélecteur pour déterminer si tous les uploads sont en pause globalement
export const isGloballyPaused = (state: RootState): boolean => {
  return state.fileUploadReducer.isGloballyPaused;
};
    
